import * as React from "react";
import { FastField } from "formik";
import { FormGroup } from "@lib/components";
import { Button } from "@lib/components";
import { RotateLoader } from "@lib/components";
import { Switch } from "@lib/components";
import { UI } from "../../../../../../../core/ui";
import { MobxComponent } from "../../../../../../../mobx/components/index";
import { inject, observer } from "mobx-react";
import { RestaurantForm } from "../../../../../../../mobx/components/restaurant-form";
import styled from "styled-components";

interface Props {
	close?: () => void;
}

type FormValues = T.Schema.Restaurant.RestaurantReservationsIntegration;

const BetaBadge = styled.span`
	display: inline-block;
	background: #f59e0b;
	color: #fff;
	font-size: 0.625rem;
	font-weight: 800;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	padding: 2px 6px;
	border-radius: 3px;
	margin-left: 8px;
	vertical-align: middle;
`;

const SectionNote = styled.p`
	font-size: 0.8125rem;
	color: #64748b;
	margin-bottom: 1.25rem;
	line-height: 1.5;
`;

@inject("store") @observer
export class SettingsFormIntegrationReservations extends MobxComponent<Props> {

	initialValues: FormValues;

	constructor(props: Props) {
		super(props);
		const r = this.injected.store.restaurant!;
		const existing = r.reservations_integration || {};
		this.initialValues = {
			enabled: existing.enabled === true,
			calendar_enabled: existing.calendar_enabled !== false,
			widget_enabled: existing.widget_enabled !== false,
			actions_enabled: existing.actions_enabled !== false,
		};
	}

	render() {
		return (
			<RestaurantForm<FormValues>
				submit={async (r, values) => {
					r.reservations_integration = values;
					const update = { $set: { "reservations_integration": values } };
					return { r, update };
				}}
				initialValues={this.initialValues}
				onSuccess={() => {
					if (this.props.close) {
						this.props.close();
					}
				}}
				onError={() => UI.notification.error("An error occurred")}
				onSuccessMessage="Settings Updated"
			>
				{({ form, error }) => {
					const { isSubmitting, setFieldValue, values } = form;
					const masterOn = values.enabled === true;
					return (
						<div className="p-4">
							<SectionNote>
								Control which reservation features are available. When a toggle is off, the corresponding feature is hidden from the dashboard and/or storefront.
							</SectionNote>

							<FastField
								name="enabled"
								render={({ field }: any) => (
									<FormGroup
										title="Enabled"
										help="Turning this on will enable the full reservation features including calendar, widget, and booking logic."
									>
										<Switch
											id="reservations-enabled-switch"
											checked={field.value === true}
											onChange={(e) =>
												setFieldValue("enabled", e.target.checked)
											}
										/>
									</FormGroup>
								)}
							/>

							{masterOn && (
								<>
									<FastField
										name="calendar_enabled"
										render={({ field }: any) => (
											<FormGroup
												title="Calendar View"
												help="Show or hide the Calendar tab on the Bookings page."
											>
												<Switch
													id="reservations-calendar-switch"
													checked={field.value !== false}
													onChange={(e) =>
														setFieldValue("calendar_enabled", e.target.checked)
													}
												/>
											</FormGroup>
										)}
									/>

									<FastField
										name="widget_enabled"
										render={({ field }: any) => (
											<FormGroup
												title="Store Widget / Booking"
												help="Show or hide the floating booking widget and /reserve embed on the storefront."
											>
												<Switch
													id="reservations-widget-switch"
													checked={field.value !== false}
													onChange={(e) =>
														setFieldValue("widget_enabled", e.target.checked)
													}
												/>
											</FormGroup>
										)}
									/>

									<FastField
										name="actions_enabled"
										render={({ field }: any) => (
											<FormGroup
												title="Quick Actions"
												help="Show or hide the Add Booking and Walk-in buttons on the Bookings page."
											>
												<Switch
													id="reservations-actions-switch"
													checked={field.value !== false}
													onChange={(e) =>
														setFieldValue("actions_enabled", e.target.checked)
													}
												/>
											</FormGroup>
										)}
									/>
								</>
							)}

							{error && <FormGroup error={error} />}

							<Button
								full={true}
								color="primary"
								type="submit"
								disabled={isSubmitting}
							>
								{isSubmitting && (
									<RotateLoader size={2} color="white" />
								)}
								{!isSubmitting && "Save"}
							</Button>
						</div>
					);
				}}
			</RestaurantForm>
		);
	}
}
